import { Typography } from '@mui/material';
import Loading from 'components/Loading';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { usePrimaryColor } from 'styles/theme';
import { useGetDraftOperations } from '../../BudgetProposal/Resource/ExpenseIncome/Operations/helpers/operationHelpers';
import { CreateDraftOperation } from './components/CreateDraftOperation';
import DraftOperationsGrid from './components/DraftOperationsGrid';
import { getDraftOperationsColumns } from './helpers/colDefinitions';
import { BudgetBys } from 'business/constants';
import Flex from 'ui/Flex/Flex';
import ConditionnalZoomIconButton from 'components/ConditionnalZoomIconButton';
import { useFullScreen } from 'contexts/FullScreenContext';
import { FALLBACK_SECONDARY_BUDGET_AXIS_DISPLAY } from 'routes/BudgetModule/components/BudgetWithProvider/SecondaryBudgetAxisSourceTableProvider';
import { translateBlock } from 'i18n';
import { pluralize } from 'helpers/formatHelper';
import { useSelectSecondaryBudgetAxisSourceTable } from 'routes/BudgetModule/components/BudgetWithProvider/SecondaryBudgetAxisSourceTableContext';

const DraftOperations: React.FC<{
    referenceYear: number;
    expenseOrIncome: string;
    budgetName: string;
    budgetBy: BudgetBys;
}> = ({ referenceYear, expenseOrIncome, budgetName, budgetBy }) => {
    const primaryColor = usePrimaryColor();
    const { t } = useTranslation();
    const { isFullScreen } = useFullScreen();
    const draftOperationsColumns = getDraftOperationsColumns({
        primaryColor,
        referenceYear,
        t,
    });

    const { data: draftOperations, isLoading } = useGetDraftOperations({
        withDetails: true,
        exercice: referenceYear,
        expenseOrIncome,
        budgetName: budgetName,
    });
    const sourceTable = useSelectSecondaryBudgetAxisSourceTable();
    if (isLoading || !draftOperations) {
        return <Loading />;
    }

    const sourceTableLabel = sourceTable
        ? translateBlock(sourceTable)
        : FALLBACK_SECONDARY_BUDGET_AXIS_DISPLAY;

    return (
        <Flex gap={2} flex={1} flexDirection="column">
            <Flex gap={2} alignItems="center">
                <ConditionnalZoomIconButton />
                <Typography variant="h5">
                    {t('budgetOverview.mantySecondaryBudgetAxisElement', {
                        sourceTable: pluralize(sourceTableLabel),
                    })}
                </Typography>
            </Flex>
            <DraftOperationsGrid
                draftOperations={draftOperations}
                draftOperationsColumns={draftOperationsColumns}
                exercice={referenceYear}
                expenseOrIncome={expenseOrIncome}
            />
            <CreateDraftOperation
                exercice={referenceYear}
                budgetBy={budgetBy}
                isDafView
                sx={{
                    alignSelf: 'flex-end',
                    ...(isFullScreen ? { mr: 2, mb: 2 } : {}),
                }}
            />
        </Flex>
    );
};

export default DraftOperations;
